import ColorPicker from "components/basic/color-picker/ColorPicker";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import React from "react";
import { T } from "services/lang/LanguageService";
import { handleRowReorderEvt, rangeBody } from "utils/utils";
import { DatasetSpecification } from "../../interfaces";
import { DashOption, LineEditorProps } from "../DatasetLineChartTypes";

export const DASH_OPTIONS = [
  { label: T("dash-type.solid"), value: "solid", strokeDasharray: "0" },
  { label: T("dash-type.dotted"), value: "dot", strokeDasharray: "2" },
  { label: T("dash-type.dashed"), value: "dash", strokeDasharray: "5" },
  { label: T("dash-type.long-dashed"), value: "longdash", strokeDasharray: "10" },
  { label: T("dash-type.dash-dot"), value: "dashdot", strokeDasharray: "15, 3, 3, 3" },
  { label: T("dash-type.long-dash-dot"), value: "longdashdot", strokeDasharray: "25, 3, 3, 3" },
];

function LineEditor({ setCurrentConfig, currentConfig }: LineEditorProps) {
  const customDropdownItem = (option: DashOption) => {
    return option ? (
      <div>
        <svg height="15" width="100">
          <line
            x1="0"
            y1="50%"
            x2="100%"
            y2="50%"
            stroke="black"
            strokeWidth="2"
            strokeDasharray={option.strokeDasharray}
          />
        </svg>
      </div>
    ) : (
      <div>{T("action.select-a-dash")}</div>
    );
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={e => {
          options.editorCallback?.(e.target.value);
        }}
        style={{ width: "100%" }}
      />
    );
  };

  const overriddenTitleBodyTemplate = (rowData: DatasetSpecification) => {
    return <div>{rowData.overriddenTitle}</div>;
  };

  const handleEditorCallback = (options: ColumnEditorOptions, value?: string | number | null) => {
    if (!currentConfig.datasets) return;

    const { identifier } = options.rowData;
    const key = options.field.split(".").at(-1) as string;

    setCurrentConfig(prev => ({
      ...prev,
      datasets: prev.datasets?.map(d =>
        d.identifier === identifier ? { ...d, line: { ...d.line, [key]: value } } : d
      ),
    }));
    options.editorCallback?.(value);
  };

  const handleRangeEditorCallback = (options: ColumnEditorOptions, newRange: Array<number>) => {
    if (!currentConfig.datasets) {
      return;
    }

    const { identifier } = options.rowData;
    const datasetIndex = currentConfig.datasets.findIndex(
      dataset => dataset.identifier === identifier
    );
    if (datasetIndex !== -1) {
      const updatedDatasets = [...currentConfig.datasets];
      updatedDatasets[datasetIndex] = { ...updatedDatasets[datasetIndex], range: newRange };
      setCurrentConfig({ ...currentConfig, datasets: updatedDatasets });
      options.editorCallback!(newRange);
    }
  };

  const lineColorEditor = (options: ColumnEditorOptions) => {
    const value = options.rowData.line?.color ?? "#FFFFFF";
    return (
      <div className="flex gap-1 align-items-center">
        <ColorPicker
          value={value}
          onValueChange={e => handleEditorCallback(options, e.target.value)}
        />
        <i
          className="pi pi-refresh ml-2 cursor-pointer"
          onClick={() => handleEditorCallback(options, undefined )}
        />
      </div>
    );
  };

  const colorBodyTemplate = (rowData: DatasetSpecification) => {
    let currentColor = currentConfig.datasets
      ? currentConfig.datasets.find(dataset => dataset.identifier === rowData.identifier)?.line
          ?.color
      : undefined;
    if (!currentColor) {
      currentColor = "#FFFFFF";
    }
    return (
      <div
        style={{
          width: "25px",
          height: "25px",
          backgroundColor: currentColor?.startsWith("#") ? currentColor : `#${currentColor}`,
          borderRadius: "5px",
          border: "1px solid #c8c8c8",
        }}
      />
    );
  };

  const numberEditor = (options: ColumnEditorOptions) => {
    return (
      <InputNumber
        inputStyle={{ width: "100%", height: "100%" }}
        value={options.rowData.line?.width}
        onValueChange={e => handleEditorCallback(options, e.target.value )}
      />
    );
  };

  const widthBodyTemplate = (rowData: DatasetSpecification) => {
    const currentWidth = currentConfig.datasets
      ? currentConfig.datasets.find(dataset => dataset.identifier === rowData.identifier)?.line
          ?.width
      : undefined;
    return <div style={{ width: "100%", height: "100%" }}>{currentWidth}</div>;
  };

  const dashEditor = (options: ColumnEditorOptions) => {
    return (
      <Dropdown
        className="w-full"
        value={options.value?.dash ?? options.rowData.line?.dash}
        placeholder={T("action.select-a-dash")}
        options={DASH_OPTIONS}
        onChange={e => handleEditorCallback(options, e.value )}
        itemTemplate={customDropdownItem}
        valueTemplate={customDropdownItem}
      />
    );
  };

  const dashBodyTemplate = (rowData: DatasetSpecification) => {
    const currentDash = currentConfig.datasets
      ? currentConfig.datasets.find(dataset => dataset.identifier === rowData.identifier)?.line
          ?.dash
      : undefined;
    const dashArray = DASH_OPTIONS.find(option => option.value === currentDash)?.strokeDasharray;
    return (
      <div>
        <svg height="15" width="100">
          <line
            x1="0"
            y1="50%"
            x2="100%"
            y2="50%"
            stroke="black"
            strokeWidth="2"
            strokeDasharray={dashArray}
          />
        </svg>
      </div>
    );
  };

  const rangeEditor = (options: ColumnEditorOptions) => {
    const currentRange = options.rowData.range || [undefined, undefined];

    return (
      <div className="flex flex-row gap-2">
        <InputNumber
          inputStyle={{ width: "100%", height: "100%" }}
          value={currentRange[0]}
          maxFractionDigits={2}
          placeholder="MIN"
          onChange={e => {
            const newRange = [e.value, currentRange[1]];
            handleRangeEditorCallback(options, newRange);
          }}
        />
        <InputNumber
          inputStyle={{ width: "100%", height: "100%" }}
          maxFractionDigits={2}
          value={currentRange[1]}
          placeholder="MAX"
          onChange={e => {
            const newRange = [currentRange[0], e.value];
            handleRangeEditorCallback(options, newRange);
          }}
        />
      </div>
    );
  };

  const rangeBodyTemplate = (rowData: DatasetSpecification) => {
    const currentRange =
      currentConfig.datasets &&
      currentConfig.datasets.find(dataset => dataset.identifier === rowData.identifier)?.range;
    return <div>{rangeBody(currentRange)}</div>;
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData } = e;
    const datasetsToUpdate = [...currentConfig.datasets!];

    const datasetIndex = datasetsToUpdate.findIndex(
      dataset => dataset.identifier === newData.identifier
    );

    if (datasetIndex !== -1) {
      datasetsToUpdate[datasetIndex] = {
        ...datasetsToUpdate[datasetIndex],
        overriddenTitle: newData.overriddenTitle,
      };

      setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
    }
  };

  const titleBodyTemplate = (rowData: DatasetSpecification) => {
    return <DatasetTitle identifier={rowData.identifier} wordBreak />;
  };

  return (
    <DataTable
      value={currentConfig.datasets}
      onRowEditComplete={onRowEditComplete}
      editMode="row"
      onRowReorder={handleRowReorderEvt(
        currentConfig.datasets,
        setCurrentConfig,
        currentConfig,
        "datasets"
      )}
      reorderableRows
    >
      <Column rowReorder style={{ width: "3%" }} />
      <Column
        field="identifier"
        header={T("word.title")}
        body={titleBodyTemplate}
        style={{ width: "30%" }}
      />
      <Column
        className="p-0"
        field="overriddenTitle"
        header={T("word.overridden-title")}
        style={{ width: "15%" }}
        editor={options => textEditor(options)}
        body={overriddenTitleBodyTemplate}
      />
      <Column
        field="line.color"
        header={T("word.color")}
        style={{ width: "15%" }}
        editor={options => lineColorEditor(options)}
        body={colorBodyTemplate}
      />
      <Column
        field="line.width"
        header={T("word.width")}
        style={{ width: "10%" }}
        editor={options => numberEditor(options)}
        body={widthBodyTemplate}
      />
      <Column
        field="line.dash"
        header={T("word.dash")}
        style={{ width: "10%" }}
        editor={options => dashEditor(options)}
        body={dashBodyTemplate}
      />
      <Column
        field="range"
        header={T("word.y-axis-range")}
        style={{ width: "20%" }}
        editor={options => rangeEditor(options)}
        body={rangeBodyTemplate}
      />
      <Column rowEditor align="center" style={{ width: "10%" }} />
    </DataTable>
  );
}

export default LineEditor;
